import { action, observable, runInAction } from 'mobx'
import remotedev from 'mobx-remotedev'
import { StoreExt } from '@utils/reactExt'
import { users } from '@services/api'
import { ProductDetails } from '@utils/purchasePopupUtils'
import { resolveCardBlockedStatus } from '@utils/cardBlockedStatus'

// Reuse a minimal placeholder so downstream consumers always get a ProductDetails shape.
const EMPTY_PRODUCT: ProductDetails = {
    id: '',
    name: '',
    price: 0,
    sku: '',
    subscriptionKey: '',
    productType: '',
    iteration_type: '',
    iteration_charge: ''
}

type RowStatus =
    | 'ACTIVE'
    | 'CANCELED'
    | 'EXPIRED'
    | 'UPGRADED'
    | 'AWAITING_PAYMENT'
    | 'AWAITING_BANK_APPROVAL'
    | 'AWAITING_BANK_TRANSFER'
    | 'BLOCKED'
    | 'CARD_BLOCKED'
    | 'REGISTERED'
    | 'INCOMPLETE'

type StoStatus = 'active' | 'inactive'

type CardBlockedRow = {
    id?: number
    user_id?: number | null
    subscription_id?: number | null
    coupon_id?: number | null
    status?: RowStatus | null
    members?: string | null
    created_at?: string
    updated_at?: string
    paid_at?: string | null
    provided_by?: number | null
    provider_percent?: number | null
    parent_provider_id?: number | null
    parent_provider_percent?: number | null
    price?: number | null
    quantity?: number | null
    terminal?: string | null
    payment_late?: string | null
    blocked_cancel?: boolean | null
    updated_by?: string | null
    transaction_id?: number | null
    transaction_details?: string | null
    dates_range?: string | null
    user_token_id?: number | null
    recurring_id?: number | null
    low_profile_code?: string | null
    portfolio_manager?: string | null
    sto_id?: number | null
    sto_status?: StoStatus | null
    terminal_name?: string | null
    type?: string
    name?: string
    sku?: string
}

type CardStatusRow = {
    agent_id?: number | string
    client_id?: number | string
    subscription_id?: number | string
    created_at?: string
    updated_at?: string
    card_approval?: string | null
}

type CreditCardStatusResponse = {
    cardStatus?: CardStatusRow
    recentCardBlockedRow?: CardBlockedRow
}

export class PurchaseStore extends StoreExt {
    private clientInfoFetcher?: () => Promise<void> | void
    // Track modal visibility and the latest card/subscription lifecycle metadata.
    @observable cardWasBlocked = false
    @observable cardCurrentlyBlocked = false
    @observable recentCardBlockedRow: CardBlockedRow | null = null
    @observable hasBankPendingOrActiveSub = false
    @observable userSubBlocked = false
    @observable lastProduct: ProductDetails = { ...EMPTY_PRODUCT }
    @observable selectedTranzilaProduct: ProductDetails = { ...EMPTY_PRODUCT }
    @observable clientInfo: Record<string, any> = {}
    @observable cardStatusResolved = false
    @observable purchaseStatusVersion = 0

    // Build the last purchased product payload that Tranzila expects.
    private createTranzilaInfo(cardRow: CardBlockedRow | null, subscriptionsList: any[] = [], isPrepayment?: boolean): ProductDetails | null {
        if (!cardRow || !cardRow.subscription_id) {
            return null
        }
        const product = subscriptionsList.find(sub => sub.id === cardRow.subscription_id)
        if (!product) {
            return null
        }

        return {
            id: cardRow.subscription_id || null,
            name: product.name || '',
            sku: product.sku || '',
            iteration_type: isPrepayment ? 'ONCE' : 'MONTHLY',
            price: typeof product.price === 'number' ? product.price : 0,
            subscriptionKey: cardRow.id || '',
            productType: 'monthly',
            iteration_charge: null
        }
    }

    // Restore the tracked fields back to their defaults when a full reset is needed.
    @action
    resetState(): void {
        this.cardWasBlocked = false
        this.cardCurrentlyBlocked = false
        this.recentCardBlockedRow = null
        this.hasBankPendingOrActiveSub = false
        this.lastProduct = { ...EMPTY_PRODUCT }
        this.selectedTranzilaProduct = { ...EMPTY_PRODUCT }
        this.clientInfo = {}
        this.cardStatusResolved = false
        this.purchaseStatusVersion = 0
    }

    // Fetch the latest credit card data and adjust observable flags accordingly.
    // Wrap the API call so every consumer can reuse the same payload interpretation.
    @action
    async checkCreditCardStatus(clientInfo: any): Promise<CreditCardStatusResponse> {
        try {
            const response = await users.getCreditCardStatus({ user_id: clientInfo.id })
            return response.data || {}
        } catch {
            return {}
        }
    }

    // Refresh the stored rows, client metadata, and last product based on the latest API response.
    @action
    async fetchRecentCardBlockedRow(clientInfo: any, subscriptionsList: any[] = []): Promise<void> {
        this.cardStatusResolved = false
        // Keep the previous blocked flags while the request is pending. They are resolved below once fresh data arrives.
        this.hasBankPendingOrActiveSub = clientInfo.subscription_status === 'ACTIVE'
        this.userSubBlocked = clientInfo.subscription_status === 'BLOCKED'

        try {
            const { cardStatus, recentCardBlockedRow } = await this.checkCreditCardStatus(clientInfo)
            const blockedStatus = resolveCardBlockedStatus<CardStatusRow, CardBlockedRow>(cardStatus, recentCardBlockedRow)
            const { normalizedCardStatus, normalizedBlockedRow, cardWasBlocked, cardCurrentlyBlocked, hasInvalidDates } =
                blockedStatus

            const tempLastProduct = this.createTranzilaInfo(normalizedBlockedRow, subscriptionsList, clientInfo && clientInfo.isPrepayment) || {
                ...EMPTY_PRODUCT
            }

            runInAction(() => {
                this.recentCardBlockedRow = normalizedBlockedRow
                this.clientInfo = { ...clientInfo }
                this.lastProduct = tempLastProduct
                this.cardWasBlocked = cardWasBlocked
                this.cardCurrentlyBlocked = cardCurrentlyBlocked
            })
            if (hasInvalidDates && normalizedCardStatus && normalizedBlockedRow) {
                // Safe default: treat as currently blocked so the UI doesn't silently hide the state
                console.warn('[PurchaseStore] Invalid date in setCardBlockedStatus', {
                    card_approval: normalizedCardStatus.card_approval,
                    created_at: normalizedBlockedRow.created_at
                })
            }
        } finally {
            runInAction(() => {
                this.cardStatusResolved = true
                this.purchaseStatusVersion += 1
            })
        }
    }

    // Guard the purchase prompt so only eligible roles see it when no active subscription exists.
    shouldShowPurchasePopup(roles: string[]): boolean {
        const safeRoles = roles || []
        return (
            !this.userSubBlocked &&
            !this.hasBankPendingOrActiveSub &&
            this.cardStatusResolved &&
            (safeRoles.includes('admin') ||
                safeRoles.includes('money') ||
                safeRoles.includes('agents_moderator') ||
                safeRoles.includes('distributors_manager'))
        )
    }
    setClientInfoFetcher(fetcher?: () => Promise<void> | void): void {
        this.clientInfoFetcher = fetcher
    }


    updateAfterRelease = async (): Promise<void> => {
        if (this.clientInfoFetcher) {
            await this.clientInfoFetcher()
        }
        runInAction(() => {
            this.hasBankPendingOrActiveSub = true
            this.purchaseStatusVersion += 1
            this.cardStatusResolved = true
        })
    }

    // Provide the most recent blocked purchase row for consumers that need the metadata.
    getRecentCardBlockedRow(): CardBlockedRow | null {
        return this.recentCardBlockedRow
    }

    // Return the previously blocked product so quick-buy state can render a summary.
    getLastProduct(): ProductDetails {
        return this.lastProduct
    }

    @action
    setSelectedTranzilaProduct(productDetails?: ProductDetails): void {
        this.selectedTranzilaProduct = productDetails ? { ...EMPTY_PRODUCT, ...productDetails } : { ...EMPTY_PRODUCT }
    }

    @action
    setCollectiveMode(value: boolean): void {
        this.collectiveMode = value
    }

    getSelectedTranzilaProduct(): ProductDetails {
        return this.selectedTranzilaProduct
    }

    // True when the Tranzila form was opened from the collective flow (no charge, add to collective on submit).
    @observable collectiveMode = false

    getCardCurrentlyBlocked(): boolean {
        return this.cardCurrentlyBlocked
    }

    // Expose a reactive version token so consumers can force recomputation after release updates.
    getPurchaseStatusVersion(): number {
        return this.purchaseStatusVersion
    }

    // Format the stored card approval date into the Hebrew locale used by the UI.
    getFormattedCardApprovalDate(): string {
        const cardApproval = this.clientInfo.card_approval
        const approvalDate = cardApproval ? new Date(cardApproval) : new Date()
        return approvalDate.toLocaleDateString('he-IL').replace(/\./g, '/')
    }
}

export default remotedev(new PurchaseStore())
